import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate, R_EVENTO, R_STATUS, R_STATUS_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ReservaFormDialog, { fmtBRL, fmtDataBR, waNumber } from "@/components/ReservaFormDialog";
import { ArrowDown, ArrowUp, ArrowUpDown, Ban, CalendarDays, CheckCircle2, Eye, Loader2, MessageCircle, Pencil, Plus, Printer, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FILTERS = { q: "", area_id: "todas", status: "confirmada", date_from: "", date_to: "" };

const SORT_KEYS = {
  numero: (r) => r.numero ?? 0,
  area_name: (r) => (r.area_name || "").toLowerCase(),
  data: (r) => `${r.data || ""} ${r.inicio || ""}`,
  inicio: (r) => r.inicio || "",
  solicitante_nome: (r) => (r.solicitante_nome || "").toLowerCase(),
  eql_label: (r) => (r.eql_label || "").toLowerCase(),
  evento_tipo: (r) => R_EVENTO[r.evento_tipo] || "",
  status: (r) => R_STATUS[r.status] || "",
  valor: (r) => r.valor ?? 0,
};

export function sortReservas(items, key, dir) {
  const get = SORT_KEYS[key] || SORT_KEYS.data;
  return [...items].sort((a, b) => {
    const va = get(a);
    const vb = get(b);
    const cmp = typeof va === "number" && typeof vb === "number"
      ? va - vb
      : String(va).localeCompare(String(vb), "pt-BR");
    return dir === "asc" ? cmp : -cmp;
  });
}

export default function ReservasPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [areas, setAreas] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem("reservas-filters") || "null");
      if (saved && typeof saved === "object") return { ...EMPTY_FILTERS, ...saved };
    } catch {}
    return EMPTY_FILTERS;
  });
  useEffect(() => {
    sessionStorage.setItem("reservas-filters", JSON.stringify(filters));
  }, [filters]);
  useEffect(() => {
    const reset = () => setFilters((f) => ({ ...f, status: "confirmada" }));
    window.addEventListener("reservas:reset-status", reset);
    return () => window.removeEventListener("reservas:reset-status", reset);
  }, []);
  const [sort, setSort] = useState({ key: "data", dir: "desc" });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.area_id !== "todas") params.area_id = filters.area_id;
    if (filters.status !== "todos") params.status = filters.status;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    api.get("/tenant/reservas", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/tenant/areas").then(({ data }) => setAreas(data)).catch(() => {});
  }, []);

  const cancelar = async () => {
    if (!motivo.trim()) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/tenant/reservas/${cancelTarget.id}`, { status: "cancelada", motivo_cancelamento: motivo.trim() });
      toast.success("Reserva cancelada");
      setCancelTarget(null);
      setMotivo("");
      setViewing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const concluir = async (r) => {
    setBusy(true);
    try {
      await api.patch(`/tenant/reservas/${r.id}`, { status: "concluida" });
      toast.success("Reserva marcada como concluída");
      setViewing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const excluir = async () => {
    setBusy(true);
    try {
      await api.delete(`/tenant/reservas/${deleteTarget.id}`);
      toast.success("Reserva excluída");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const openWhatsReserva = (r) => {
    const num = waNumber(r.solicitante_telefone);
    if (!num) return;
    const msg = encodeURIComponent(
      `Olá ${r.solicitante_nome || ""}! Reserva #${String(r.numero).padStart(4, "0")} — ${r.area_name} em ${fmtDataBR(r.data)}, das ${r.inicio} às ${r.fim}. Status: ${R_STATUS[r.status] || r.status}.`);
    const w = 420;
    const h = 720;
    const left = Math.max(0, window.screen.availWidth - w - 24);
    window.open(`https://wa.me/${num}?text=${msg}`, "helpdesk-whatsapp",
      `popup=yes,width=${w},height=${h},left=${left},top=60`);
  };

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  const openPrint = () => {
    const qs = new URLSearchParams();
    if (filters.q.trim()) qs.set("q", filters.q.trim());
    if (filters.area_id !== "todas") qs.set("area_id", filters.area_id);
    if (filters.status !== "todos") qs.set("status", filters.status);
    if (filters.date_from) qs.set("date_from", filters.date_from);
    if (filters.date_to) qs.set("date_to", filters.date_to);
    qs.set("sort", sort.key);
    qs.set("dir", sort.dir);
    window.open(`/app/reservas/print?${qs.toString()}`, "_blank");
  };

  const Th = ({ k, label, className = "" }) => (
    <th className={`px-3 py-3 text-xs font-medium text-zinc-400 ${className}`}>
      <button type="button" data-testid={`reservas-sort-${k}`} onClick={() => toggleSort(k)}
        className="inline-flex items-center gap-1 hover:text-zinc-100 transition-colors duration-150">
        {label}
        {sort.key === k
          ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3 text-indigo-400" /> : <ArrowDown className="h-3 w-3 text-indigo-400" />)
          : <ArrowUpDown className="h-3 w-3 text-zinc-600" />}
      </button>
    </th>
  );

  const hasFilters = filters.q || filters.area_id !== "todas" || filters.status !== "confirmada" || filters.date_from || filters.date_to;

  if (!items) return <Spinner />;

  const sorted = sortReservas(items, sort.key, sort.dir);

  return (
    <div data-testid="reservas-page">
      <PageHeader title="Reservas" subtitle="Agendamento de áreas comuns — churrasqueira, salão de festas, quadras e demais espaços">
        <Button data-testid="reservas-print-button" variant="outline" onClick={openPrint}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
          <Printer className="h-4 w-4 mr-1.5" /> Imprimir/PDF
        </Button>
        <Button data-testid="new-reserva-button" onClick={() => { setEditing(null); setFormOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Nova Reserva
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mb-4 space-y-3" data-testid="reservas-filters">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input data-testid="reservas-filter-q" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Buscar por Nº, solicitante, EQL, quadra ou lote…"
            className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Espaço</Label>
            <Select value={filters.area_id} onValueChange={(v) => setFilters({ ...filters, area_id: v })}>
              <SelectTrigger data-testid="reservas-filter-area" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-52">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="reservas-filter-area-todas" value="todas">Todos</SelectItem>
                {areas.map((a) => (
                  <SelectItem key={a.id} data-testid={`reservas-filter-area-${a.id}`} value={a.id}>{a.code} — {a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="reservas-filter-status" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="reservas-filter-status-todos" value="todos">Todos</SelectItem>
                {Object.entries(R_STATUS).map(([k, label]) => (
                  <SelectItem key={k} data-testid={`reservas-filter-status-${k}`} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Data de</Label>
            <Input type="date" data-testid="reservas-filter-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">até</Label>
            <Input type="date" data-testid="reservas-filter-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          {hasFilters && (
            <Button variant="ghost" data-testid="reservas-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={CalendarDays} title="Nenhuma reserva encontrada"
          subtitle="Registre reservas de áreas comuns em nome dos moradores." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm min-w-[1050px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <Th k="numero" label="Nº" />
                <Th k="area_name" label="Espaço" />
                <Th k="data" label="Data" />
                <Th k="inicio" label="Horário" />
                <Th k="solicitante_nome" label="Solicitante" />
                <Th k="eql_label" label="Unidade" />
                <Th k="evento_tipo" label="Evento" />
                <Th k="status" label="Status" />
                <Th k="valor" label="Taxa" />
                <th className="px-3 py-3 text-xs font-medium text-zinc-400 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} data-testid={`reserva-row-${r.id}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                  <td className="px-3 py-3 font-mono text-xs text-zinc-400">#{String(r.numero).padStart(4, "0")}</td>
                  <td className="px-3 py-3 text-zinc-100 max-w-44 truncate" title={r.area_name}>{r.area_name}</td>
                  <td className="px-3 py-3 text-zinc-300 text-xs whitespace-nowrap">{fmtDataBR(r.data)}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs whitespace-nowrap">{r.inicio}–{r.fim}</td>
                  <td className="px-3 py-3 text-zinc-300 text-xs max-w-40 truncate">{r.solicitante_nome}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs max-w-28 truncate">{r.eql_label ? r.eql_label.split(" — ").pop() : "—"}</td>
                  <td className="px-3 py-3 text-zinc-400 text-xs">{R_EVENTO[r.evento_tipo] || r.evento_tipo}</td>
                  <td className="px-3 py-3"><Badge data-testid={`reserva-status-${r.id}`} className={`${R_STATUS_STYLE[r.status]} border-0 text-[11px] font-medium`}>{R_STATUS[r.status]}</Badge></td>
                  <td className="px-3 py-3 text-zinc-400 text-xs whitespace-nowrap">{fmtBRL(r.valor)}</td>
                  <td className="px-3 py-3 text-right">
                    <span className="inline-flex items-center gap-1">
                      {waNumber(r.solicitante_telefone) && (
                        <Button data-testid={`reserva-whats-${r.id}`} variant="ghost" size="icon" title="Enviar confirmação via WhatsApp"
                          onClick={() => openWhatsReserva(r)}
                          className="h-8 w-8 text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800">
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button data-testid={`reserva-view-${r.id}`} variant="ghost" size="icon" title="Visualizar"
                        onClick={() => setViewing(r)}
                        className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {r.status !== "cancelada" && (
                        <>
                          <Button data-testid={`reserva-edit-${r.id}`} variant="ghost" size="icon" title="Editar"
                            onClick={() => { setEditing(r); setFormOpen(true); }}
                            className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button data-testid={`reserva-cancel-${r.id}`} variant="ghost" size="icon" title="Cancelar reserva"
                            onClick={() => { setCancelTarget(r); setMotivo(""); }}
                            className="h-8 w-8 text-zinc-400 hover:text-amber-400 hover:bg-zinc-800">
                            <Ban className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {user?.role === "tenant_admin" && (
                        <Button data-testid={`reserva-delete-${r.id}`} variant="ghost" size="icon" title="Excluir"
                          onClick={() => setDeleteTarget(r)}
                          className="h-8 w-8 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ReservaFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} areas={areas} onSaved={load} />

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="reserva-view-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Reserva #{String(viewing?.numero || 0).padStart(4, "0")}</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">Detalhes do agendamento.</DialogDescription>
          </DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <Badge className={`${R_STATUS_STYLE[viewing.status]} border-0 text-[11px] font-medium`}>{R_STATUS[viewing.status]}</Badge>
                <span className="text-zinc-400 text-xs">criada por {viewing.created_by_name} em {fmtDate(viewing.created_at)}</span>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-1.5" data-testid="reserva-view-details">
                <p className="text-zinc-100 font-medium">{viewing.area_name}</p>
                <p className="text-zinc-300 text-xs">{fmtDataBR(viewing.data)} · {viewing.inicio}–{viewing.fim} · {R_EVENTO[viewing.evento_tipo]}</p>
                <p className="text-zinc-400 text-xs">Solicitante: <span className="text-zinc-200">{viewing.solicitante_nome}</span>
                  {viewing.solicitante_email ? ` · ${viewing.solicitante_email}` : ""}{viewing.solicitante_telefone ? ` · ${viewing.solicitante_telefone}` : ""}</p>
                <p className="text-zinc-400 text-xs">Unidade: <span className="text-zinc-200">{viewing.eql_label || "—"}</span>
                  {viewing.quadra ? ` · Quadra ${viewing.quadra}` : ""}{viewing.lote ? ` · Lote ${viewing.lote}` : ""}</p>
                <p className="text-zinc-400 text-xs">Convidados: {viewing.convidados} · Taxa: <span className="text-zinc-200">{fmtBRL(viewing.valor)}</span></p>
                {viewing.notas && <p className="text-zinc-400 text-xs">Observações: {viewing.notas}</p>}
                {viewing.status === "cancelada" && (
                  <p className="text-amber-400/90 text-xs" data-testid="reserva-view-cancel-info">
                    Cancelada por {viewing.cancelada_por} em {fmtDate(viewing.cancelada_em)} — {viewing.motivo_cancelamento}
                  </p>
                )}
              </div>
              {viewing.status !== "cancelada" && (
                <div className="flex justify-end gap-2">
                  {viewing.status === "confirmada" && (
                    <Button data-testid="reserva-concluir-button" variant="outline" disabled={busy} onClick={() => concluir(viewing)}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-zinc-100 rounded-md">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Marcar concluída
                    </Button>
                  )}
                  <Button data-testid="reserva-view-cancel-button" variant="ghost" onClick={() => { setCancelTarget(viewing); setMotivo(""); }}
                    className="text-amber-400 hover:text-amber-300 hover:bg-zinc-800 rounded-md">
                    <Ban className="h-4 w-4 mr-1.5" /> Cancelar reserva
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="reserva-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Cancelar reserva</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              A reserva #{String(cancelTarget?.numero || 0).padStart(4, "0")} ficará registrada como cancelada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs text-zinc-400">Motivo do cancelamento *</Label>
            <Input data-testid="reserva-cancel-motivo" value={motivo} maxLength={300}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder='Ex.: "Morador desistiu da data"'
              className="mt-1.5 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
          </div>
          <div className="flex justify-end gap-2">
            <Button data-testid="reserva-cancel-back" variant="ghost" onClick={() => setCancelTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">Voltar</Button>
            <Button data-testid="reserva-cancel-confirm" onClick={cancelar} disabled={busy}
              className="bg-amber-600 hover:bg-amber-500 text-white rounded-md transition-colors duration-200">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar cancelamento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="reserva-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir reserva</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir a reserva <span className="text-zinc-200 font-medium">#{String(deleteTarget?.numero || 0).padStart(4, "0")}</span>? A exclusão é permanente e fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button data-testid="reserva-delete-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">Voltar</Button>
            <Button data-testid="reserva-delete-confirm" onClick={excluir} disabled={busy}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
